/**
 * Care plan attachment on a Care Concierge case (Feature 106, requirement v4 item 12).
 *
 * Its own actions rather than part of the case form: a file upload is not something to hold in
 * memory until someone presses Save, and a failed save should not take the file with it.
 *
 * Whether the advisor can read it is the case's share switch — the same one that governs the
 * notes. Uploading shares nothing on its own, and the copy here says so, because "I attached it
 * to their case" is exactly the assumption that would leak a care plan.
 */

import { useRef, useState } from 'react';
import { FileText, Trash2, Upload } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/components/Toast';
import { downloadBlob } from '@/shared/utils/downloadBlob';
import {
  CARE_PLAN_MAX_BYTES,
  deleteCarePlan,
  fetchCarePlan,
  uploadCarePlan,
} from '../../services/careConciergeCasesApi';

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
  (error as { message?: string })?.message ??
  fallback;

type CarePlanFieldProps = {
  caseId: string;
  hasCarePlan: boolean;
  sharedWithAdvisor: boolean;
  /** Refreshes the case, carrying whether a plan is now attached. */
  onChanged: (hasCarePlan: boolean) => void;
};

export const CarePlanField = ({
  caseId,
  hasCarePlan,
  sharedWithAdvisor,
  onChanged,
}: CarePlanFieldProps) => {
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;

    // Checked here as well as on the server so a 10 MB upload is refused instantly rather than
    // after the client has spent a minute sending it.
    if (file.size > CARE_PLAN_MAX_BYTES) {
      showToast('A care plan must be 10 MB or smaller.', 'error');
      return;
    }

    setBusy(true);
    try {
      await uploadCarePlan(caseId, file);
      showToast(
        sharedWithAdvisor
          ? 'Care plan attached. The advisor can see it, because this case is shared.'
          : 'Care plan attached. It is not visible to the advisor until you share the case.',
        'success',
      );
      onChanged(true);
    } catch (error) {
      showToast(errorMessage(error, 'Could not upload the care plan.'), 'error');
    } finally {
      setBusy(false);
      // Cleared so choosing the same file again still fires a change event.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const { blob, fileName } = await fetchCarePlan(caseId);
      downloadBlob(blob, fileName);
    } catch (error) {
      showToast(errorMessage(error, 'Could not download the care plan.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await deleteCarePlan(caseId);
      showToast('Care plan removed.', 'success');
      onChanged(false);
    } catch (error) {
      showToast(errorMessage(error, 'Could not remove the care plan.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Label htmlFor="cc-case-care-plan">Care plan (PDF, max 10 MB)</Label>

      <input
        ref={inputRef}
        id="cc-case-care-plan"
        type="file"
        accept="application/pdf"
        className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm"
        onChange={(event) => void handleFile(event.target.files?.[0])}
        disabled={busy}
      />

      {hasCarePlan && (
        <div className="mt-2 flex items-center gap-2 text-sm">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">A care plan is attached.</span>
          <Button type="button" variant="ghost" size="sm" onClick={handleDownload} disabled={busy}>
            <Upload className="mr-1 h-3 w-3 rotate-180" />
            Download
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={handleRemove} disabled={busy}>
            <Trash2 className="mr-1 h-3 w-3" />
            Remove
          </Button>
        </div>
      )}

      <p className="mt-1 text-xs text-muted-foreground">
        Uploading a care plan does not share it. The advisor sees it only when the case is shared
        below, along with the notes.
      </p>
    </div>
  );
};
